import React, { useEffect, useRef, useState } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./FirestoreService.scss";

const FirestoreService = () => {
  const { Id } = useParams(); // eventState は使用していないようなので削除
  const location = useLocation();
  const navigate = useNavigate();

  // location.state が null/undefined の場合を安全に処理
  const state = location.state ?? {};
  const targetDataName = (state.databasename ?? "default").toLowerCase();
  const targetTitle = state.posttitle ?? "項目";
  const targetItems = Array.isArray(state.targetitems) ? state.targetitems : [];
  const EditTargetData = state.targetData ?? null;

  const isEditing = Boolean(Id);

  const [targetData, setTargetData] = useState({
    order: "",
    title: "",
    eventday: "",
    selectedYear: new Date().getFullYear().toString(),
    selectedMonth: "1",
    selectedDay: "1",
    content: "",
    linkUrl: "",
    youtubeUrl: "",
    image: "",
    other: "",
    created_at: "",
    updated_at: "",
  });

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);

  // state が存在しない場合は一覧に戻す（無反応防止）
  useEffect(() => {
    if (!location.state) {
      console.warn("編集に必要なデータがありません。一覧に戻ります");
      navigate(`/${targetDataName}`);
    }
  }, [location.state, navigate, targetDataName]);

  // 編集時の初期データ設定
  useEffect(() => {
    if (isEditing && EditTargetData) {
      setTargetData({
        ...EditTargetData,
        selectedYear:
          EditTargetData.selectedYear ?? new Date().getFullYear().toString(),
        selectedMonth: EditTargetData.selectedMonth ?? "1",
        selectedDay: EditTargetData.selectedDay ?? "1",
      });
    }
  }, [isEditing, EditTargetData]);

  // テキストエリアの高さ自動調整（content が使われていないようなので削除可能）
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [targetData.content]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    resizeImage(file, 800, 800, 0.7).then(setImage);
  };

  const resizeImage = (file, maxWidth, maxHeight, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            },
            file.type,
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTargetData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    let imageUrl = targetData.image || "";

    if (image) {
      try {
        const storageRef = ref(storage, `images/${Date.now()}.jpg`);
        const uploadTask = uploadBytesResumable(storageRef, image);
        await new Promise((resolve, reject) => {
          uploadTask.on("state_changed", null, reject, async () => {
            imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
            resolve();
          });
        });
      } catch (err) {
        console.error("画像アップロード失敗:", err);
        setLoading(false);
        return;
      }
    }

    const eventdatestring = `${targetData.selectedYear}年${targetData.selectedMonth}月${targetData.selectedDay}日`;

    try {
      const dataToSave = {
        ...targetData,
        image: imageUrl,
        eventdatestring,
        order: Number(targetData.order) || 0,
      };

      if (isEditing) {
        dataToSave.updated_at = serverTimestamp();
        await updateDoc(doc(db, targetDataName, Id), dataToSave);
      } else {
        dataToSave.created_at = serverTimestamp();
        await addDoc(collection(db, targetDataName), dataToSave);
      }

      navigate(`/${targetDataName}`);
    } catch (err) {
      console.error("保存エラー:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(`本当に${targetData.title || "この項目"}を削除しますか？`)
    )
      return;
    try {
      await deleteDoc(doc(db, targetDataName, Id));
      navigate(`/${targetDataName}`);
    } catch (err) {
      console.error("削除エラー:", err);
    }
  };

  return (
    <div className="firestoreservice-container">
      <div className="firestoreservice-content">
        <h2>
          <span className="firestoreservice-content-title">
            {isEditing ? `${targetTitle}を編集` : `${targetTitle}を追加`}
          </span>
          <div
            className="createpost-close-button"
            onClick={() => navigate(`/${targetDataName}`)}
          >
            ×
          </div>
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="firestoreservice-field">
            {targetItems?.some?.((item) => item === "order") && (
              <>
                <h3>表示順</h3>
                <input
                  type="number"
                  name="order"
                  value={targetData.order ?? ""}
                  onChange={handleChange}
                />
              </>
            )}

            <h3>タイトル</h3>
            <input
              type="text"
              name="title"
              value={targetData.title ?? ""}
              onChange={handleChange}
              required
            />

            <h3>内容</h3>
            <textarea
              ref={textareaRef}
              name="content"
              value={targetData.content ?? ""}
              onChange={handleChange}
              placeholder="内容を入力"
              required
            />

            {targetItems?.some?.((item) => item === "linkUrl") && (
              <>
                <h3>リンクURL</h3>
                <input
                  type="url"
                  name="linkUrl"
                  value={targetData.linkUrl ?? ""}
                  onChange={handleChange}
                />
              </>
            )}

            {targetItems?.some?.((item) => item === "image") && (
              <>
                <h3>画像</h3>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {targetData.image && (
                  <img
                    src={targetData.image}
                    alt="プレビュー"
                    style={{ maxWidth: "200px", marginTop: "1rem" }}
                  />
                )}
              </>
            )}

            {targetItems?.some?.((item) => item === "youtubeUrl") && (
              <>
                <h3>YouTube URL</h3>
                <input
                  type="url"
                  name="youtubeUrl"
                  value={targetData.youtubeUrl ?? ""}
                  onChange={handleChange}
                />
              </>
            )}

            <button type="submit" disabled={loading}>
              {isEditing ? "保存" : loading ? "投稿中..." : "投稿"}
            </button>

            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                style={{ color: "red" }}
              >
                削除
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FirestoreService;
